import { StyleSheet } from 'aphrodite';

/**
 * Shared Aphrodite styles for the org surfaces (switcher, members,
 * settings, invite modal, redeem page).
 *
 * Color tokens cascade:
 *   1. `--td-*` — defined by dashboard.css (the dashboard route is
 *                where most of these surfaces render).
 *   2. `--*` — defined by editor.css / tile-ui.css for editor routes
 *              where the switcher also appears in the top bar.
 *   3. Hex fallback — so the component renders sanely if neither
 *                     stylesheet is loaded (e.g. a test harness).
 *
 * Variable cascade syntax: `var(--td-foo, var(--foo, #default))`.
 */

const c = {
  white: 'var(--td-white, var(--white, #ffffff))',
  gray50: 'var(--td-gray-50, var(--gray-50, #f9f9f9))',
  gray100: 'var(--td-gray-100, var(--gray-100, #f3f3f3))',
  // 200/300/600/800 aren't in dashboard.css; fall back gracefully.
  gray200: 'var(--td-gray-200, var(--gray-200, #e6e6e6))',
  gray300: 'var(--td-gray-300, var(--gray-300, #d6d6d6))',
  gray400: 'var(--td-gray-400, var(--gray-400, #a8a8a8))',
  gray500: 'var(--td-gray-500, var(--gray-500, #777777))',
  gray600: 'var(--td-gray-600, var(--gray-600, #5e5e5e))',
  gray700: 'var(--td-gray-700, var(--gray-700, #484848))',
  gray900: 'var(--td-gray-900, var(--gray-900, #1a1a1a))',
  blue: 'var(--td-tile-blue, var(--tile-blue, #2068a8))',
  blueHover: 'var(--td-tile-blue-hover, var(--tile-blue-hover, #185490))',
  blueWash: 'var(--td-tile-blue-wash, var(--tile-blue-wash, #eef4fa))',
  mono: "var(--mono, 'SF Mono', Menlo, Consolas, monospace)",
};

export const orgStyles = StyleSheet.create({
  // ─── Switcher ────────────────────────────────────────────────────
  switcher: {
    position: 'relative' as const,
    display: 'inline-flex',
    alignItems: 'center',
    background: c.white,
  },
  switcherBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 12px',
    border: `1px solid ${c.gray200}`,
    borderRadius: 6,
    background: c.white,
    color: c.gray900,
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    ':hover': { borderColor: c.gray400 },
  },
  switcherAvatar: {
    width: 20,
    height: 20,
    borderRadius: 4,
    background: 'linear-gradient(135deg, #4F8DEC, #1060E0)',
    color: c.white,
    fontSize: 10,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textTransform: 'uppercase' as const,
  },
  switcherChevron: { color: c.gray500, fontSize: 10 },
  popover: {
    position: 'absolute' as const,
    top: 36,
    left: 0,
    minWidth: 240,
    maxWidth: 320,
    background: c.white,
    border: `1px solid ${c.gray200}`,
    borderRadius: 8,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    zIndex: 100,
    padding: 4,
  },
  popoverItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 12,
    color: c.gray900,
    ':hover': { background: c.gray50 },
  },
  popoverItemActive: {
    background: c.blueWash,
    color: c.blue,
  },
  popoverItemRole: {
    marginLeft: 'auto',
    fontSize: 10,
    color: c.gray500,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
  },
  popoverDivider: { height: 1, background: c.gray100, margin: '4px 0' },
  popoverAction: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 12,
    color: c.gray700,
    ':hover': { background: c.gray50 },
  },

  // ─── Page shell ──────────────────────────────────────────────────
  page: {
    maxWidth: 900,
    margin: '0 auto',
    padding: '32px 24px',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: 24,
    paddingBottom: 16,
    borderBottom: `1px solid ${c.gray100}`,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: c.gray900,
  },
  pageSubtitle: {
    fontSize: 12,
    color: c.gray500,
    marginTop: 4,
  },

  // ─── Member table ────────────────────────────────────────────────
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: 13,
  },
  th: {
    textAlign: 'left' as const,
    padding: '10px 12px',
    color: c.gray500,
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    borderBottom: `1px solid ${c.gray100}`,
  },
  td: {
    padding: '12px',
    borderBottom: `1px solid ${c.gray50}`,
    color: c.gray900,
  },
  roleChip: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    background: c.gray100,
    color: c.gray700,
  },
  roleOwner: { background: '#FFF3E0', color: '#8A4A00' },
  roleAdmin: { background: '#EEF4FA', color: '#1060E0' },
  roleDeveloper: { background: '#E8F5E9', color: '#1B5E20' },
  rolePm: { background: '#F3EAFB', color: '#6B21A8' },
  roleViewer: { background: c.gray100, color: c.gray600 },

  // ─── Buttons ─────────────────────────────────────────────────────
  btn: {
    padding: '8px 14px',
    border: 'none',
    borderRadius: 6,
    background: c.blue,
    color: c.white,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    ':hover': { background: c.blueHover },
    ':disabled': { opacity: 0.5, cursor: 'not-allowed' },
  },
  btnGhost: {
    padding: '8px 14px',
    border: `1px solid ${c.gray200}`,
    borderRadius: 6,
    background: c.white,
    color: c.gray700,
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    ':hover': { borderColor: c.gray400 },
  },
  btnDanger: {
    padding: '8px 14px',
    border: '1px solid #FFB4B4',
    borderRadius: 6,
    background: c.white,
    color: '#7A0000',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    ':hover': { background: '#FFF4F4' },
  },
  btnIcon: {
    padding: 4,
    border: 'none',
    borderRadius: 4,
    background: 'transparent',
    color: c.gray500,
    cursor: 'pointer',
    fontSize: 12,
    ':hover': { background: c.gray100, color: c.gray900 },
  },

  // ─── Modal ───────────────────────────────────────────────────────
  modalBackdrop: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: c.white,
    borderRadius: 8,
    padding: 24,
    width: 'min(480px, 92vw)',
    boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
  },
  modalTitle: { fontSize: 16, fontWeight: 700, marginBottom: 6 },
  modalDesc: { fontSize: 12, color: c.gray500, marginBottom: 16 },
  modalActions: {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
    marginTop: 20,
  },
  field: { marginBottom: 14 },
  label: {
    display: 'block',
    fontSize: 11,
    color: c.gray700,
    marginBottom: 4,
    fontWeight: 600,
  },
  input: {
    width: '100%',
    padding: '8px 10px',
    border: `1px solid ${c.gray200}`,
    borderRadius: 4,
    fontSize: 13,
    fontFamily: 'inherit',
    outline: 'none',
    ':focus': { borderColor: c.blue },
  },
  inputMono: { fontFamily: c.mono, fontSize: 11 },
  select: {
    width: '100%',
    padding: '8px 10px',
    border: `1px solid ${c.gray200}`,
    borderRadius: 4,
    fontSize: 13,
    background: c.white,
  },

  // ─── Error / status ──────────────────────────────────────────────
  errorBox: {
    padding: 10,
    background: '#FFF4F4',
    border: '1px solid #FFB4B4',
    borderRadius: 4,
    color: '#7A0000',
    fontSize: 12,
    marginBottom: 12,
  },
  empty: {
    padding: 32,
    textAlign: 'center' as const,
    color: c.gray500,
    fontSize: 13,
  },
  copyBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: 8,
    background: c.gray50,
    border: `1px solid ${c.gray200}`,
    borderRadius: 4,
    fontFamily: c.mono,
    fontSize: 11,
    wordBreak: 'break-all' as const,
  },
});
